<template>
  <v-container>
    <!-- Title -->
    <v-row class="mb-1">
      <v-col>
        <div class="text-h5 font-weight-bold">ລາຍງານການຈ່າຍບິນ</div>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-row align="center">
      <v-col cols="12" sm="auto">
        <v-menu
          v-model="startDateMenu"
          :close-on-content-click="true"
          min-width="auto"
          offset-y
          transition="scale-transition"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="startDate"
              clearable
              dense
              hide-details
              label="ເລີ່ມວັນທີ"
              outlined
              prepend-inner-icon="mdi-calendar"
              readonly
              style="min-width: 170px"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="startDate" />
        </v-menu>
      </v-col>
      <v-col cols="12" sm="auto">
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="true"
          min-width="auto"
          offset-y
          transition="scale-transition"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endDate"
              clearable
              dense
              hide-details
              label="ຫາວັນທີ"
              outlined
              prepend-inner-icon="mdi-calendar"
              readonly
              style="min-width: 170px"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="endDate" />
        </v-menu>
      </v-col>
      <v-col cols="12" sm="auto">
        <v-select
          v-model="paymentMethod"
          :items="paymentMethods"
          clearable
          dense
          hide-details
          item-text="text"
          item-value="value"
          label="ປະເພດການຈ່າຍ"
          outlined
          style="min-width: 180px"
        />
      </v-col>
      <v-col cols="12" sm="auto">
        <v-select
          v-model="customerType"
          :items="customerTypes"
          dense
          hide-details
          item-text="text"
          item-value="value"
          label="ປະເພດລູກຄ້າ"
          outlined
          style="min-width: 180px"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          depressed
          height="40"
          @click="fetchData"
        >
          <v-icon left small>mdi-magnify</v-icon>
          ຄົ້ນຫາ
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="success"
          depressed
          height="40"
          @click="exportExcel"
          :loading="exporting"
        >
          <v-icon left small>mdi-microsoft-excel</v-icon>
          Export
        </v-btn>
      </v-col>
    </v-row>

    <!-- Total Summary Card -->
    <v-row class="mt-6">
      <v-col cols="12" sm="6" md="4">
        <v-card class="rounded-lg" outlined>
          <v-card-text class="pa-5">
            <div class="d-flex justify-space-between align-center mb-3">
              <span class="text-subtitle-1 font-weight-medium">ລວມທັງໝົດ</span>
              <v-chip color="green" dark small>
                <v-icon left x-small>mdi-check-circle</v-icon>
                ຈ່າຍແລ້ວ
              </v-chip>
            </div>
            <div class="text-h4 font-weight-bold mb-1">
              {{ formatCurrency(summary.total.total_amount) }}
              <span class="text-subtitle-1 grey--text font-weight-regular">LAK</span>
            </div>
            <div class="text-body-2 grey--text">
              {{ formatNumber(summary.total.bill_count) }} ບິນ
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Payment method breakdown -->
    <v-row v-if="summary.by_payment_method.length > 0" class="mt-2">
      <v-col cols="12" class="pb-0">
        <span class="text-subtitle-1 font-weight-medium grey--text text--darken-1">ແຍກຕາມປະເພດການຈ່າຍ</span>
      </v-col>
    </v-row>

    <v-row v-if="summary.by_payment_method.length > 0">
      <v-col
        v-for="item in summary.by_payment_method"
        :key="item.payment_method"
        cols="12"
        sm="4"
        md="3"
        lg="2"
      >
        <v-card class="rounded-lg" outlined height="100%">
          <v-card-text class="pa-4">
            <div class="d-flex justify-space-between align-center mb-3">
              <span class="text-body-2 font-weight-medium text--primary">{{ item.payment_method_la }}</span>
            </div>
            <div class="text-h6 font-weight-bold mb-1">
              {{ formatCurrency(item.total_amount) }}
              <span class="text-caption grey--text font-weight-regular">LAK</span>
            </div>
            <div class="text-body-2 grey--text">
              {{ formatNumber(item.bill_count) }} ບິນ
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- No data -->
    <v-row v-if="!loading && summary.by_payment_method.length === 0" class="mt-4">
      <v-col>
        <v-alert type="info" text>
          ບໍ່ມີຂໍ້ມູນ. ກະລຸນາເລືອກວັນທີແລ້ວກົດ "ຄົ້ນຫາ"
        </v-alert>
      </v-col>
    </v-row>

    <!-- Bank Reconciliation Loading -->
    <v-row v-if="reconciliationLoading" class="mt-6">
      <v-col cols="12">
        <v-card outlined class="rounded-lg">
          <v-card-title>
            <v-icon left>mdi-bank-check</v-icon>
            ກວດສອບກັບ EOD Reconciliation
          </v-card-title>
          <v-card-text class="text-center py-6">
            <v-progress-circular indeterminate color="primary" class="mr-3" />
            <span class="text-body-1">ກຳລັງໂຫຼດຂໍ້ມູນ EOD Reconciliation...</span>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Bank Reconciliation Section -->
    <v-row v-if="reconciliation && !reconciliationLoading" class="mt-6">
      <v-col cols="12">
        <v-card outlined class="rounded-lg">
          <v-card-title>
            <v-icon left>mdi-bank-check</v-icon>
            ກວດສອບກັບ EOD Reconciliation
            <v-spacer />
            <v-chip
              v-if="reconciliation.has_eod"
              small
              :color="reconciliation.batch_status === 'closed' ? 'green' : 'orange'"
              dark
            >
              {{ reconciliation.batch_status }}
            </v-chip>
          </v-card-title>
          <v-card-text>
            <!-- No EOD alert -->
            <v-alert v-if="!reconciliation.has_eod" type="warning" text>
              <v-icon left>mdi-alert</v-icon>
              {{ reconciliation.message }}
            </v-alert>

            <!-- No statement uploaded alert -->
            <v-alert v-if="reconciliation.has_eod && !reconciliation.all_statements_uploaded" type="warning" text dense class="mb-4">
              <v-icon small left>mdi-alert</v-icon>
              ບາງບັນຊີຍັງບໍ່ໄດ້ອັບໂຫຼດ bank statement
            </v-alert>

            <div v-if="reconciliation.has_eod">
              <!-- Summary comparison -->
              <v-simple-table dense class="mb-4">
                <thead>
                  <tr>
                    <th class="text--primary">ລາຍການ</th>
                    <th class="text-right text--primary">ຈຳນວນ (LAK)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="font-weight-bold">ຍອດລວມລາຍງານບິນ</td>
                    <td class="text-right font-weight-bold">{{ formatCurrency(reconciliation.total_billing_payment) }}</td>
                  </tr>
                  <tr class="text--secondary">
                    <td class="pl-6">- ມີ bank transaction (ກວດສອບໄດ້)</td>
                    <td class="text-right">{{ formatCurrency(reconciliation.billing_with_bank_txn) }}</td>
                  </tr>
                  <tr class="text--secondary">
                    <td class="pl-6">- ບໍ່ມີ bank transaction (ເງິນສົດ/ໂອນ)</td>
                    <td class="text-right">{{ formatCurrency(reconciliation.billing_without_bank_txn) }}</td>
                  </tr>
                  <tr>
                    <td colspan="2"><v-divider /></td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">ຍອດລວມ Bank Statement (EOD)</td>
                    <td class="text-right font-weight-bold">{{ formatCurrency(reconciliation.total_bank_received) }}</td>
                  </tr>
                  <tr>
                    <td colspan="2"><v-divider /></td>
                  </tr>
                  <tr :class="reconciliation.difference === 0 ? 'green--text' : 'red--text'">
                    <td class="font-weight-bold">ຜົນຕ່າງ</td>
                    <td class="text-right font-weight-bold">{{ formatCurrency(reconciliation.difference) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Bank breakdown -->
              <div class="text-subtitle-2 mb-2">ແຍກຕາມບັນຊີ</div>
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text--primary">ບັນຊີ</th>
                    <th class="text-right text--primary">ຍອດ Statement</th>
                    <th class="text-right text--primary">ກົງກັນ</th>
                    <th class="text-right text--primary">ອະນຸມັດແລ້ວ</th>
                    <th class="text-right text--primary">ລໍຖ້າ</th>
                    <th class="text--primary">ສະຖານະ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="bank in reconciliation.bank_breakdown" :key="bank.bank_account">
                    <td>
                      {{ bank.bank_account }}
                      <v-icon v-if="!bank.has_statement" small color="orange" class="ml-1">mdi-alert</v-icon>
                    </td>
                    <td class="text-right">{{ formatCurrency(bank.statement_total) }}</td>
                    <td class="text-right green--text">{{ formatCurrency(bank.matched_total) }}</td>
                    <td class="text-right blue--text">{{ formatCurrency(bank.approved_total) }}</td>
                    <td class="text-right" :class="bank.unmatched_total > 0 ? 'orange--text' : ''">{{ formatCurrency(bank.unmatched_total) }}</td>
                    <td>
                      <v-chip x-small :color="bank.status === 'completed' ? 'green' : (bank.has_statement ? 'blue' : 'grey')" dark>
                        {{ bank.status === 'completed' ? 'ສຳເລັດ' : (bank.has_statement ? 'ກຳລັງດຳເນີນ' : 'ລໍຖ້າ') }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr style="border-top: 2px solid">
                    <td class="font-weight-bold text--primary">ລວມ</td>
                    <td class="text-right font-weight-bold text--primary">{{ formatCurrency(bankBreakdownTotal.statement) }}</td>
                    <td class="text-right font-weight-bold green--text">{{ formatCurrency(bankBreakdownTotal.matched) }}</td>
                    <td class="text-right font-weight-bold blue--text">{{ formatCurrency(bankBreakdownTotal.approved) }}</td>
                    <td class="text-right font-weight-bold" :class="bankBreakdownTotal.unmatched > 0 ? 'orange--text' : ''">{{ formatCurrency(bankBreakdownTotal.unmatched) }}</td>
                    <td></td>
                  </tr>
                </tbody>
              </v-simple-table>

              <!-- Link to EOD -->
              <div class="mt-3 text-right">
                <v-btn small text color="primary" :href="'/eod/ldb-reconciliation/' + reconciliation.batch_id" target="_blank">
                  <v-icon small left>mdi-open-in-new</v-icon>
                  ເປີດ EOD Reconciliation
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'BillingPaymentSummary',
  data() {
    return {
      startDate: new Date().toISOString().substr(0, 10),
      endDate: new Date().toISOString().substr(0, 10),
      paymentMethod: null,
      customerType: null,
      startDateMenu: false,
      endDateMenu: false,
      loading: false,
      exporting: false,
      summary: {
        total: { bill_count: 0, total_amount: 0 },
        by_payment_method: [],
      },
      // EOD Reconciliation box: off until EOD is in daily use (it also compares a multi-day range with one batch).
      showEodReconciliation: false,
      reconciliation: null,
      reconciliationLoading: false,
      paymentMethods: [
        { text: 'ເງິນສົດ', value: 'cash' },
        { text: 'ເງິນໂອນ', value: 'bcel' },
        { text: 'bcel bill payment', value: 'bcel_online' },
        { text: 'bcel deeplink', value: 'bcel_deeplink' },
        { text: 'jdb deeplink', value: 'jdb_deeplink' },
        { text: 'LDB in app', value: 'LDB_IN_APP' },
        { text: 'LDB merchant', value: 'LDB_MERCHANT' },
        { text: 'U-Money in app', value: 'UMONEY_IN_APP' },
      ],
      customerTypes: [
        { text: 'ທັງໝົດ', value: null },
        { text: 'ລູກຄ້າຄົວເຮືອນ', value: 'home' },
        { text: 'ລູກຄ້າທຸລະກິດ', value: 'company' },
      ],
    };
  },
  computed: {
    bankBreakdownTotal() {
      if (!this.reconciliation || !this.reconciliation.bank_breakdown) {
        return {
          statement: 0, matched: 0, approved: 0, unmatched: 0,
        };
      }
      return this.reconciliation.bank_breakdown.reduce((acc, b) => ({
        statement: acc.statement + Number(b.statement_total || 0),
        matched: acc.matched + Number(b.matched_total || 0),
        approved: acc.approved + Number(b.approved_total || 0),
        unmatched: acc.unmatched + Number(b.unmatched_total || 0),
      }), {
        statement: 0, matched: 0, approved: 0, unmatched: 0,
      });
    },
  },
  created() {},
  methods: {
    async fetchData() {
      this.loading = true;
      this.$store.commit('Loading_State', true);
      try {
        const res = await this.$axios.get('v2/report-billing-payment-summary', {
          params: this.buildParams(),
        });
        this.summary = res.data.data;
        if (this.showEodReconciliation) this.fetchReconciliation();
      } catch (e) {
        console.error(e);
        if (e.response && e.response.status === 403) {
          this.$store.commit('Toast_State', { value: true, color: 'error', msg: 'ບໍ່ມີສິດເຂົ້າເຖິງ' });
        }
      } finally {
        this.loading = false;
        this.$store.commit('Loading_State', false);
      }
    },
    async fetchReconciliation() {
      this.reconciliationLoading = true;
      this.reconciliation = null;
      try {
        const res = await this.$axios.get('v2/report-billing-payment-reconciliation', {
          params: this.buildParams(),
        });
        this.reconciliation = res.data.data;
      } catch (e) {
        console.error(e);
        this.reconciliation = null;
      } finally {
        this.reconciliationLoading = false;
      }
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const res = await this.$axios.get('v2/report-billing-payment-export', {
          params: this.buildParams(),
          timeout: 120000,
        });
        const link = res.data.data.download_link;
        if (link) window.open(link, '_blank');
      } catch (e) {
        console.error(e);
        this.$store.commit('Toast_State', { value: true, color: 'error', msg: 'Export ລົ້ມເຫຼວ' });
      } finally {
        this.exporting = false;
      }
    },
    buildParams() {
      const params = {};
      if (this.startDate) params.start_date = this.startDate;
      if (this.endDate) params.end_date = this.endDate;
      if (this.paymentMethod) params.payment_method = this.paymentMethod;
      if (this.customerType) params.customer_type = this.customerType;
      return params;
    },
    formatCurrency(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-US');
    },
    formatNumber(val) {
      if (!val) return '0';
      return Number(val).toLocaleString('en-US');
    },
  },
};
</script>
